import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "@/lib/next-compat";
import { Drawer } from "@/pages/a/monitoring/articles/detail";
import { Ch } from "@/components/Fa";
import { Link } from "react-router-dom";
import { Panel } from "@/components/Panel";
import { Tile } from "@/components/Tile";

const TONE_ID: Record<string, string> = { positive: "Positif", neutral: "Netral", negative: "Negatif" };

const SEV: Record<string, [string, string]> = { viral: ["#8a4f9e", "Viral"], critical: ["#b00020", "Kritis"], medium: ["#e2711d", "Medium"], low: ["#6b7a99", "Low"] };
const ST_OPT = [["open", "Open"], ["responding", "Responding"], ["monitoring", "Monitoring"], ["closed", "Closed"]] as const;

const CT_OPT = [["news", "Counter News"], ["social", "Counter Post"], ["pressrelease", "Siaran Pers"], ["other", "Lainnya"]] as const;
const nf = (x: number) => Intl.NumberFormat("id-ID", { notation: "compact" }).format(x ?? 0);

// AI Analysis Suite — kind → label/ikon/deskripsi. Backend: POST /api/v1/issues/:id/analyze { kind }
const KINDS = [
  { k: "summary", label: "Ringkasan Isu", icon: "fa-file-lines", desc: "Kenapa terjadi · postingan pemicu · kronologi · poin kunci" },
  { k: "strakom", label: "Strategi Komunikasi", icon: "fa-bullseye", desc: "Status · tindakan · angle konten · pantangan" },
  { k: "holding", label: "Holding Statement", icon: "fa-comment-dots", desc: "Pernyataan sementara cepat sebelum klarifikasi penuh" },
  { k: "press", label: "Siaran Pers", icon: "fa-newspaper", desc: "Press release resmi siap edit" },
  { k: "content", label: "Strategi Konten & Kanal", icon: "fa-share-nodes", desc: "Kanal/media/sosmed paling berdampak (berbasis data)" },
  { k: "impact", label: "Prediksi Dampak", icon: "fa-chart-line", desc: "Estimasi durasi isu berbalik netral/positif" },
] as const;
type Kind = (typeof KINDS)[number]["k"];

/** Renderer markdown ringan (## heading, > blockquote, - bullet, **tebal**, | tabel |) untuk output AI. */
function Md({ text }: { text: string }) {
  if (!text) return null;
  const lines = text.split("\n");
  const out: ReactNode[] = [];
  let list: string[] = [];
  const inline = (s: string) => s.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>").replace(/\*(.+?)\*/g, "<i>$1</i>");
  const flush = () => { if (list.length) { out.push(<ul key={`u${out.length}`} style={{ margin: "4px 0 10px", paddingLeft: 20 }}>{list.map((li, i) => <li key={i} style={{ marginBottom: 3 }} dangerouslySetInnerHTML={{ __html: inline(li) }} />)}</ul>); list = []; } };
  // Deteksi baris tabel markdown: dimulai & diakhiri "|" (atau minimal ada "|" di dalam).
  const isRow = (s: string) => /^\s*\|.*\|\s*$/.test(s);
  const isSep = (s: string) => /^\s*\|?[\s:|-]+\|?\s*$/.test(s) && s.includes("-");
  const cells = (s: string) => s.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
  for (let idx = 0; idx < lines.length; idx++) {
    const l = lines[idx].trimEnd();
    // ---- TABEL: header | separator | body ----
    if (isRow(l) && idx + 1 < lines.length && isSep(lines[idx + 1])) {
      flush();
      const header = cells(l);
      const body: string[][] = [];
      idx += 2; // lewati header + separator
      while (idx < lines.length && isRow(lines[idx])) { body.push(cells(lines[idx])); idx++; }
      idx--; // koreksi karena for akan ++ lagi
      out.push(
        <div key={`t${out.length}`} style={{ overflowX: "auto", margin: "8px 0 12px" }}>
          <table className="avio-md-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--text-sm)" }}>
            <thead>
              <tr>{header.map((h, i) => <th key={i} style={{ textAlign: "left", padding: "6px 10px", borderBottom: "2px solid var(--color-border-strong)", background: "var(--color-surface-sunken)", whiteSpace: "nowrap", fontWeight: "var(--weight-semibold)" as any }} dangerouslySetInnerHTML={{ __html: inline(h) }} />)}</tr>
            </thead>
            <tbody>
              {body.map((row, ri) => (
                <tr key={ri} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  {header.map((_, ci) => <td key={ci} style={{ padding: "6px 10px", verticalAlign: "top", lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: inline(row[ci] ?? "") }} />)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    if (/^##\s+/.test(l)) { flush(); out.push(<h4 key={out.length} style={{ margin: "14px 0 4px", fontSize: "var(--text-base)", color: "var(--color-text)" }}>{l.replace(/^##\s+/, "")}</h4>); }
    else if (/^#\s+/.test(l)) { flush(); out.push(<h3 key={out.length} style={{ margin: "12px 0 4px" }}>{l.replace(/^#\s+/, "")}</h3>); }
    else if (/^>\s?/.test(l)) { flush(); out.push(<blockquote key={out.length} style={{ margin: "6px 0", padding: "6px 12px", borderLeft: "3px solid var(--color-border-strong)", background: "var(--color-surface-sunken)", fontStyle: "italic" }} dangerouslySetInnerHTML={{ __html: inline(l.replace(/^>\s?/, "")) }} />); }
    else if (/^[-*]\s+/.test(l)) { list.push(l.replace(/^[-*]\s+/, "")); }
    else if (/^\d+\.\s+/.test(l)) { list.push(l.replace(/^\d+\.\s+/, "")); }
    else if (l.trim() === "") { flush(); }
    else { flush(); out.push(<p key={out.length} style={{ margin: "4px 0", lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: inline(l) }} />); }
  }
  flush();
  return <div style={{ fontSize: "var(--text-base)" }}>{out}</div>;
}

export default function IssueDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [d, setD] = useState<any>(null);
  const [busy, setBusy] = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [activeKind, setActiveKind] = useState<Kind>("summary");
  const [draft, setDraft] = useState<Record<string, string>>({}); // teks per-kind yang sedang diedit
  const [editMode, setEditMode] = useState(false); // toggle pratinjau ↔ edit mentah pada field yang sama
  const [cu, setCu] = useState({ url: "", counterType: "news", publishedAt: new Date().toISOString().slice(0, 10), note: "" });

  async function load() {
    const r = await fetch(`/api/v1/issues/${id}`);
    if (r.status === 401) { location.href = "/login"; return; }
    const j = await r.json();
    setD(j.data);
    const strat = j.data?.strategy ?? {};
    setDraft({
      summary: strat.summary?.text ?? "", strakom: strat.strakom?.text ?? strat.text ?? "",
      holding: strat.holding?.text ?? "", press: strat.press?.text ?? "",
      content: strat.content?.text ?? "", impact: strat.impact?.text ?? "",
    });
    if (j.data && !strat.summary?.text) genKind("summary"); // ringkasan otomatis saat pertama dibuka
  }
  useEffect(() => { load(); }, [id]);
  async function patch(body: any) { await fetch(`/api/v1/issues/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }); load(); }

  // Generate satu jenis analisis AI (summary/strakom/holding/press/content/impact).
  async function genKind(kind: Kind) {
    setBusy(kind);
    const r = await fetch(`/api/v1/issues/${id}/analyze`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ kind }) });
    const j = await r.json();
    setBusy("");
    if (!r.ok) { alert(j.error); return; }
    setDraft((p) => ({ ...p, [kind]: j.data.text }));
    setActiveKind(kind);
    load();
  }
  // Simpan editan analis ke kind aktif.
  async function saveKind(kind: Kind) {
    setBusy("save");
    const prev = (d?.strategy && typeof d.strategy === "object") ? d.strategy : {};
    const sec = { ...(prev[kind] ?? {}), text: draft[kind] ?? "", editedAt: new Date().toISOString() };
    const next: any = { ...prev, [kind]: sec };
    if (kind === "strakom") next.text = draft[kind] ?? ""; // back-compat
    await patch({ strategy: next });
    setBusy("");
  }
  async function addCounter() {
    if (!cu.url.trim()) return;
    setBusy("counter");
    const r = await fetch(`/api/v1/issues/${id}/counters`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(cu) });
    const j = await r.json();
    setBusy("");
    if (!r.ok) { alert(j.error); return; }
    if (j.matched) alert("URL cocok dengan data ter-ingest — engagement counter ini akan terpantau otomatis.");
    setCu({ url: "", counterType: "news", publishedAt: new Date().toISOString().slice(0, 10), note: "" });
    load();
  }
  if (!d) return <div className="avio-rise"><div className="avio-sk" style={{ height: 160, borderRadius: "var(--radius-md)" }} /></div>;
  const maxT = Math.max(1, ...(d.timeline ?? []).map((x: any) => x.c));
  const counterDays = new Set((d.counters ?? []).map((c: any) => new Date(new Date(c.publishedAt).getTime() + 7 * 3600e3).toISOString().slice(0, 10)));
  const ms = (d.mentions ?? []) as any[];
  const negCount = ms.filter((m) => m.sentimentLabel === "negative").length;
  const sevMeta: Record<string, { bg: string; fg: string }> = {
    viral: { bg: "var(--status-danger-bg)", fg: "var(--status-danger)" },
    critical: { bg: "var(--status-danger-bg)", fg: "var(--status-danger)" },
    medium: { bg: "var(--status-warn-bg)", fg: "var(--status-warn)" },
    low: { bg: "var(--gray-100, #F1F5F9)", fg: "var(--color-text-secondary)" },
  };
  const sm = sevMeta[d.severity] ?? sevMeta.low;

  return (
    <div className="avio-rise">
      <div>
        <Link to="/a/issues" className="avio-t-sm"><i className="fa-solid fa-arrow-left" /> Semua issue</Link>
        <div style={{ display: "flex", gap: "var(--space-6)", alignItems: "center", flexWrap: "wrap", marginTop: "var(--space-4)" }}>
          <h1 className="avio-page-title" style={{ margin: 0, flex: 1, minWidth: 260 }}>{d.title}</h1>
          <span className="avio-chip" data-shape="pill" style={{ background: sm.bg, color: sm.fg }}>{SEV[d.severity]?.[1] ?? d.severity}</span>
          <select className="avio-select" data-size="sm" value={d.severity} onChange={(e) => patch({ severity: e.target.value })}>
            {Object.entries(SEV).map(([k, v]) => <option key={k} value={k}>{(v as any)[1]}</option>)}
          </select>
          <select className="avio-select" data-size="sm" value={d.status} onChange={(e) => patch({ status: e.target.value })}>
            {ST_OPT.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select>
        </div>
        {d.severityAuto && (
          <div className="avio-t-sm avio-muted" style={{ marginTop: "var(--space-4)" }}>
            Saran sistem: <b>{SEV[d.severityAuto]?.[1]}</b> — {d.severityWhy}
          </div>
        )}
      </div>

      <div className="avio-tiles">
        <Tile c={1} icon="fa-solid fa-paperclip" label="Bukti tertaut" value={ms.length} sub="artikel & post" />
        <Tile c={2} icon="fa-solid fa-circle-exclamation" label="Bukti negatif" value={negCount}
          sub={ms.length ? `${((negCount / ms.length) * 100).toFixed(0)}% dari bukti` : "—"} />
        <Tile c={3} icon="fa-solid fa-shield-halved" label="Counter action" value={(d.counters ?? []).length} sub="rilis tandingan" />
        <Tile c={5} icon="fa-solid fa-robot" label="Analisis AI" value={KINDS.filter((x) => (d.strategy ?? {})[x.k]?.text).length} sub={`dari ${KINDS.length} jenis`} />
      </div>

      <div className="avio-grid">
        <div data-col="8">
          <Panel icon="fa-solid fa-chart-line" title="Pemantauan penanganan"
            hint="bar = mention/hari · merah = porsi negatif · garis oranye = tanggal counter">
            {d.verdict && (
              <div className="avio-banner" data-tone={d.verdict.label === "Membaik" ? "info" : d.verdict.label === "Memburuk" ? "danger" : "warning"} style={{ marginBottom: "var(--space-6)" }}>
                <i className="fa-solid fa-scale-balanced avio-banner__icon" />
                <div>
                  Negatif sebelum counter <b>{d.verdict.before}%</b> → sesudah <b>{d.verdict.after}%</b> — <b>{d.verdict.label}</b>.
                  <span className="avio-sub"> Ini korelasi, bukan bukti kausal.</span>
                </div>
              </div>
            )}
            {(d.timeline ?? []).length === 0 ? (
              <div className="avio-empty">
                <i className="fa-regular fa-chart-bar avio-empty__icon" />
                <div className="avio-empty__title">Belum ada bukti tertaut.</div>
                <div className="avio-empty__hint">Tambahkan dari tombol issue di detail artikel atau post.</div>
              </div>
            ) : (
              <div>
                <div className="avio-cols" style={{ height: 180 }}>
                  {(d.timeline ?? []).map((x: any) => (
                    <div key={x.day} className="avio-col"
                      style={{ borderLeft: counterDays.has(x.day) ? "2px solid var(--mark-c2)" : "2px solid transparent" }}>
                      <div className="avio-col__tip">
                        <b>{x.day.slice(5)}</b> · {x.c} mention<br />{x.neg} negatif{counterDays.has(x.day) ? " · counter dirilis" : ""}
                      </div>
                      <div style={{ height: `${(x.c / maxT) * 100}%`, display: "flex", flexDirection: "column", gap: 2, justifyContent: "flex-end" }}>
                        <div className="avio-col__seg" style={{ height: `${x.c ? (x.neg / x.c) * 100 : 0}%`, background: "var(--mark-neg)" }} />
                        <div className="avio-col__seg" style={{ height: `${x.c ? ((x.c - x.neg) / x.c) * 100 : 0}%`, background: "var(--mark-c1)" }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="avio-axis">
                  <span>{(d.timeline ?? [])[0]?.day.slice(5)}</span>
                  <span>{counterDays.size} counter dirilis</span>
                  <span>{(d.timeline ?? [])[(d.timeline ?? []).length - 1]?.day.slice(5)}</span>
                </div>
              </div>
            )}
          </Panel>
        </div>

        <div data-col="4">
          <Panel icon="fa-solid fa-shield-halved" title="Counter action" hint="rilis tandingan">
            <div style={{ display: "grid", gap: "var(--space-4)", marginBottom: "var(--space-6)" }}>
              <input className="avio-input" value={cu.url} onChange={(e) => setCu({ ...cu, url: e.target.value })} placeholder="URL counter news / post / siaran pers" />
              <div style={{ display: "flex", gap: "var(--space-4)", flexWrap: "wrap" }}>
                <select className="avio-select" data-size="sm" value={cu.counterType} onChange={(e) => setCu({ ...cu, counterType: e.target.value })}>
                  {CT_OPT.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
                </select>
                <input className="avio-input" type="date" value={cu.publishedAt} onChange={(e) => setCu({ ...cu, publishedAt: e.target.value })} />
              </div>
              <input className="avio-input" value={cu.note} onChange={(e) => setCu({ ...cu, note: e.target.value })} placeholder="Catatan (opsional)" />
              <button className="avio-btn" data-variant="primary" data-size="sm" onClick={addCounter} disabled={busy === "counter"}>
                <i className="fa-solid fa-plus" /> Tambah counter
              </button>
            </div>
            <div className="avio-list">
              {(d.counters ?? []).map((c: any) => (
                <div key={c.id} className="avio-item" style={{ paddingLeft: 0, paddingRight: 0 }}>
                  <span className="avio-item__main">
                    <a className="avio-item__title" href={c.url} target="_blank" rel="noreferrer">{c.url}</a>
                    <span className="avio-item__meta">
                      {new Date(new Date(c.publishedAt).getTime() + 7 * 3600e3).toISOString().slice(0, 10)} ·
                      {CT_OPT.find(([k]) => k === c.counterType)?.[1] ?? c.counterType}
                      {c.matchedMentionId ? " · terpantau" : ""}{c.note ? ` · ${c.note}` : ""}
                    </span>
                  </span>
                  <button className="avio-btn" data-variant="ghost" data-size="sm" style={{ color: "var(--sent-neg)" }}
                    onClick={() => fetch(`/api/v1/issues/${id}/counters`, { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: c.id }) }).then(load)}>
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
              ))}
              {(d.counters ?? []).length === 0 && (
                <div className="avio-empty"><i className="fa-regular fa-shield avio-empty__icon" /><div className="avio-empty__title">Belum ada counter action.</div></div>
              )}
            </div>
          </Panel>
        </div>

        <div data-col="9">
          <Panel icon="fa-solid fa-robot" title="AI Analysis Suite"
            hint={(() => { const s = (d.strategy ?? {})[activeKind]; return s?.generatedAt ? `AVIO AI · ${new Date(s.generatedAt).toLocaleString("id-ID")}${s.editedAt ? " · diedit analis" : ""}` : "draf AI — wajib ditinjau & diedit analis sebelum dipublikasikan"; })()}
            action={
              <button className="avio-btn" data-variant="secondary" data-size="sm" onClick={() => genKind(activeKind)} disabled={busy === activeKind}>
                <i className={busy === activeKind ? "fa-solid fa-circle-notch fa-spin" : "fa-solid fa-wand-magic-sparkles"} />
                {busy === activeKind ? " Menyusun…" : (draft[activeKind] ? " Generate ulang" : " Generate")}
              </button>
            }>
            {/* Tab pemilih jenis analisis */}
            <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", marginBottom: "var(--space-5)" }}>
              {KINDS.map((kd) => {
                const has = !!(d.strategy ?? {})[kd.k]?.text;
                const on = activeKind === kd.k;
                return (
                  <button key={kd.k} className="avio-btn" data-variant={on ? "primary" : "ghost"} data-size="sm"
                    onClick={() => { setActiveKind(kd.k); setEditMode(false); }} title={kd.desc}
                    style={{ position: "relative" }}>
                    <i className={`fa-solid ${kd.icon}`} /> {kd.label}
                    {has && <i className="fa-solid fa-circle-check" style={{ marginLeft: 6, fontSize: 10, color: on ? "inherit" : "var(--sent-pos)" }} />}
                  </button>
                );
              })}
            </div>
            <div className="avio-t-sm avio-muted" style={{ marginBottom: "var(--space-4)" }}>
              {KINDS.find((k) => k.k === activeKind)?.desc}
            </div>

            {busy === activeKind && !draft[activeKind] ? (
              <div className="avio-empty"><i className="fa-solid fa-circle-notch fa-spin avio-empty__icon" /><div className="avio-empty__title">AI sedang menyusun {KINDS.find((k) => k.k === activeKind)?.label}…</div></div>
            ) : !draft[activeKind] ? (
              <div className="avio-empty">
                <i className="fa-regular fa-wand-magic-sparkles avio-empty__icon" />
                <div className="avio-empty__title">Belum ada draf {KINDS.find((k) => k.k === activeKind)?.label}.</div>
                <div className="avio-empty__hint">Klik Generate — AI menyusun dari bukti tertaut & statistik nyata, lalu edit sesuai kebijakan instansi.</div>
              </div>
            ) : (
              <>
                {/* Satu field: pratinjau ter-render ATAU edit mentah (toggle) */}
                {editMode ? (
                  <textarea autoFocus className="avio-input" value={draft[activeKind]} onChange={(e) => setDraft((p) => ({ ...p, [activeKind]: e.target.value }))} rows={20}
                    style={{ width: "100%", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "var(--text-sm)", lineHeight: 1.6, minHeight: 300, maxHeight: 520 }} />
                ) : (
                  <div style={{ background: "var(--color-surface-sunken)", borderRadius: "var(--radius-md)", padding: "var(--space-4) var(--space-6)", minHeight: 120, maxHeight: 520, overflowY: "auto" }}>
                    <Md text={draft[activeKind]} />
                  </div>
                )}
                <div style={{ marginTop: "var(--space-5)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "var(--space-4)", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: "var(--space-3)" }}>
                    <button className="avio-btn" data-variant={editMode ? "ghost" : "secondary"} data-size="sm" onClick={() => setEditMode(false)} title="Lihat hasil ter-render">
                      <i className="fa-solid fa-eye" /> Pratinjau
                    </button>
                    <button className="avio-btn" data-variant={editMode ? "secondary" : "ghost"} data-size="sm" onClick={() => setEditMode(true)} title="Edit teks langsung di sini">
                      <i className="fa-solid fa-pen" /> Edit
                    </button>
                    <button className="avio-btn" data-variant="ghost" data-size="sm" onClick={() => navigator.clipboard?.writeText(draft[activeKind]).then(() => {}, () => {})}>
                      <i className="fa-solid fa-copy" /> Salin
                    </button>
                  </div>
                  <button className="avio-btn" data-variant="primary" data-size="sm" onClick={() => saveKind(activeKind)} disabled={busy === "save"}>
                    <i className="fa-solid fa-floppy-disk" /> {busy === "save" ? "Menyimpan…" : "Simpan editan"}
                  </button>
                </div>
              </>
            )}
          </Panel>
        </div>

        <div data-col="3">
          <Panel icon="fa-solid fa-paperclip" title="Bukti tertaut" hint={`${ms.length} item`} flush>
            <div className="avio-list" style={{ maxHeight: 460, overflowY: "auto" }}>
              {ms.map((m: any) => (
                <div key={m.id} className="avio-item">
                  <span className="avio-item__main" onClick={() => setDetailId(m.id)} style={{ cursor: "pointer" }}>
                    <span className="avio-item__title">{((m.title || m.content || "(tanpa judul)").replace(/\s+/g, " ")).slice(0, 110)}</span>
                    <span className="avio-item__meta">
                      {new Date(new Date(m.publishedAt).getTime() + 7 * 3600e3).toISOString().slice(0, 10)} ·
                      <Ch t={m.sourceType} /> {m.vendor === "neticle" ? `${nf(m.likes ?? 0)} like` : m.sourceType}
                    </span>
                  </span>
                  {m.sentimentLabel && <span className="avio-chip" data-sentiment={m.sentimentLabel}><span className="avio-chip__label">{TONE_ID[m.sentimentLabel] ?? m.sentimentLabel}</span></span>}
                  <button className="avio-btn" data-variant="ghost" data-size="sm" title="Lepas tautan" style={{ color: "var(--sent-neg)" }}
                    onClick={() => fetch(`/api/v1/issues/${id}/link`, { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mentionId: m.id }) }).then(load)}>
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
              ))}
              {ms.length === 0 && <div className="avio-empty"><i className="fa-regular fa-file avio-empty__icon" /><div className="avio-empty__title">Belum ada bukti tertaut.</div></div>}
            </div>
          </Panel>
        </div>

        {(d.status === "closed" || d.resolution) && (
          <div data-col="12">
            <Panel icon="fa-solid fa-circle-check" title="Resolusi">
              <textarea className="avio-input" defaultValue={d.resolution ?? ""} onBlur={(e) => patch({ resolution: e.target.value })} rows={3}
                placeholder="Catatan penutupan issue…" style={{ width: "100%", fontFamily: "inherit" }} />
            </Panel>
          </div>
        )}
      </div>
      {detailId && <Drawer id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
